import { X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { AccentButton, NeutralButton } from '../../../../../../shared/Buttons';
import {
  Entity,
  PatchVendorContactInfoRequest,
} from '../../../../../../types/apiTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';
type FormData = {
  managementFirstName: string;
  managementLastName: string;
  managementEmail: string;
  managementPhone: string;
  managementPosition: string;
};

export const EditAccountInfoModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { register, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      managementFirstName: '',
      managementLastName: '',
      managementEmail: '',
      managementPhone: '',
      managementPosition: '',
    },
  });
  const { userRecord } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      userRecord?.userType === Entity.Vendor &&
      userRecord?.contactInfos?.length === 2
    ) {
      reset({
        managementFirstName: userRecord?.contactInfos[1]?.first_name,
        managementLastName: userRecord?.contactInfos[1]?.last_name,
        managementEmail: userRecord?.contactInfos[1]?.email,
        managementPhone: userRecord?.contactInfos[1]?.phone_number,
        managementPosition: userRecord?.contactInfos[1]?.position,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecord]);

  const onSubmit = (data: FormData) => {
    if (userRecord?.userType === Entity.Vendor) {
      updateVendorContact(userRecord?.contactInfos[1]?.vendor_contact_id, data);
    }
  };

  const updateVendorContact = async (
    vendor_contact_id: string,
    recordInfo: FormData
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/vendorContactInfos/' + vendor_contact_id,
        'patch',
        {
          email: recordInfo?.managementEmail,
          first_name: recordInfo?.managementFirstName,
          last_name: recordInfo?.managementLastName,
          phone_number: recordInfo?.managementPhone,
          position: recordInfo?.managementPosition,
        } as PatchVendorContactInfoRequest,
        'Error occurred while updating management contact info.'
      );
      const data = response?.data?.data?.vendorContactInfo;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        response?.status === HttpStatusCode.Ok
      ) {
        onClose();
        window.location.reload();
        toast.success('Management contact info updated successfully.');
      } else {
        toast.error('Error occurred while updating management contact info.');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while updating management contact info: ',
        e
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[95%] md:max-w-[80%] lg:max-w-[60%]">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Update Management Contact Info
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              <form
                className="flex h-full w-full flex-col gap-3 overflow-y-auto border-[1px] border-bordergray p-6"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Management Contact First Name
                  </label>
                  <input
                    type="text"
                    {...register('managementFirstName', {})}
                    placeholder="Enter first name"
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Management Contact Last Name
                  </label>
                  <input
                    type="text"
                    {...register('managementLastName', {})}
                    placeholder="Enter last name"
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Management Contact Phone Number
                  </label>
                  <input
                    type="text"
                    {...register('managementPhone', {})}
                    placeholder="Enter phone number"
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Management Contact Email
                  </label>
                  <input
                    type="text"
                    {...register('managementEmail', {})}
                    placeholder="Enter email"
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Management Position
                  </label>
                  <input
                    type="text"
                    {...register('managementPosition', {})}
                    placeholder="Enter position"
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                  />
                </div>
              </form>
              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  onClick={handleSubmit(onSubmit)} // Use handleSubmit to trigger form submission
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px]"
                >
                  {!loading ? (
                    'Update'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default EditAccountInfoModal;
